import { useEffect, useState } from "react";
import { Smartphone, Plus, Trash2, Wifi, WifiOff, Loader2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatMacInput } from "@/lib/mac";
import { formatCountdown } from "@/lib/format";

export default function CustomerDevices() {
  const [devices, setDevices] = useState([]);
  const [activeSub, setActiveSub] = useState(null);
  const [open, setOpen] = useState(false);
  const [mac, setMac] = useState("");
  const [name, setName] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editMac, setEditMac] = useState("");
  const [editName, setEditName] = useState("");
  const [addError, setAddError] = useState("");
  const [editError, setEditError] = useState("");
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);

  const load = async () => {
    const [{ data: d }, { data: me }] = await Promise.all([
      api.get("/customer/devices"),
      api.get("/customer/me"),
    ]);
    setDevices(d);
    setActiveSub(me.active_subscription || null);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!mac.trim()) { setAddError("Enter a MAC address"); return; }
    setAddError("");
    try {
      await api.post("/customer/devices", { mac_address: mac, device_name: name });
      setOpen(false); setMac(""); setName("");
      await load();
      toast.success("Device registered");
    } catch (e) {
      setAddError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!confirm("Remove this device?")) return;
    try {
      await api.delete(`/customer/devices/${id}`);
      await load();
      toast.success("Removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const openEdit = (d) => {
    setEditId(d.id);
    setEditName(d.device_name || "");
    setEditMac(d.mac_address);
    setEditError("");
  };

  const saveEdit = async () => {
    const device = devices.find((d) => d.id === editId);
    const body = { device_name: editName };
    if (!device.is_connected) body.mac_address = editMac;
    setEditError("");
    try {
      await api.patch(`/customer/devices/${editId}`, body);
      setEditId(null);
      await load();
      toast.success("Device updated");
    } catch (e) {
      setEditError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const disconnect = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/customer/devices/${id}/disconnect`);
      await load();
      toast.success("Device disconnected");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const connect = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/customer/devices/${id}/connect`);
      await load();
      toast.success("Device connected");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const connectedCount = devices.filter((d) => d.is_connected).length;
  const quota = activeSub?.simultaneous_devices || 0;

  return (
    <div data-testid="customer-devices-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#43B02A]">// MY DEVICES</div>
          <h1 className="mt-1 font-heading font-black text-4xl">Devices on your account</h1>
          <p className="mt-2 text-[#57606A]">Devices connect automatically when your M-Pesa receipt code is entered on the captive portal. Register a device here to name it ahead of time.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setAddError(""); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-device-btn" className="h-11 rounded-full bg-[#43B02A] hover:bg-[#368F22] text-white font-bold shrink-0">
              <Plus className="h-4 w-4 mr-1" /> ADD DEVICE
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white rounded-none max-w-md">
            <DialogHeader>
              <DialogTitle>Register a device</DialogTitle>
              <DialogDescription>Look up your MAC address in your Wi-Fi settings.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">MAC Address</Label>
                <Input data-testid="device-mac-input" value={mac} onChange={(e) => { setMac(formatMacInput(e.target.value)); setAddError(""); }}
                       type="text" inputMode="text" autoComplete="off" autoCapitalize="characters"
                       className="mt-1 h-12 rounded-none font-mono" placeholder="AA:BB:CC:11:22:33" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">Device name (optional)</Label>
                <Input data-testid="device-name-input" value={name} onChange={(e) => setName(e.target.value)}
                       className="mt-1 h-12 rounded-none" placeholder="Home phone" />
              </div>
              {addError && (
                <div data-testid="add-device-error" className="border border-[#FF3B30]/30 bg-[#FF3B30]/5 px-3 py-2 text-sm text-[#FF3B30]">
                  {addError}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
              <Button data-testid="device-save-btn" onClick={add} className="rounded-none bg-[#43B02A] hover:bg-[#368F22] text-white font-bold">
                REGISTER
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {activeSub ? (
        <div className="mt-6 border border-[#E1E4E8] bg-[#F7F7F5] px-5 py-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-[#57606A]">
            Your <span className="font-medium text-[#1A1C20]">{activeSub.package_name}</span> plan allows{" "}
            <span className="font-medium text-[#1A1C20]">{quota}</span> device{quota === 1 ? "" : "s"} online at once.
          </div>
          <div className="font-mono text-sm font-bold text-[#43B02A] tabular-nums shrink-0">{connectedCount} / {quota} connected</div>
        </div>
      ) : (
        <div className="mt-6 border border-[#E1E4E8] bg-[#F7F7F5] px-5 py-3 text-sm text-[#57606A]">
          No active subscription — buy a package to connect a device.
        </div>
      )}

      <div className="mt-4 border border-[#E1E4E8] bg-white divide-y divide-[#E1E4E8]">
        {devices.length === 0 && <div className="p-10 text-center text-sm text-[#57606A]">No devices registered yet.</div>}
        {devices.map((d) => (
          <div key={d.id} data-testid={`device-row-${d.id}`} className="px-5 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 shrink-0 grid place-items-center bg-[#F7F7F5]"><Smartphone className="h-5 w-5 text-[#57606A]" /></div>
              <div className="min-w-0">
                <div className="font-medium truncate">{d.device_name || "Unnamed device"}</div>
                <div className="font-mono text-xs text-[#57606A] truncate">{d.mac_address}</div>
                {d.expires_at && (
                  <div className="font-mono text-[11px] text-[#57606A] mt-0.5 tabular-nums" data-testid={`device-remaining-${d.id}`}>
                    {new Date(d.expires_at).getTime() - now > 0
                      ? `${formatCountdown(new Date(d.expires_at).getTime() - now)} left on this device`
                      : "Time expired on this device"}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className={`font-mono text-[10px] uppercase tracking-[0.15em] flex items-center gap-1.5 ${d.is_connected ? "text-[#43B02A]" : "text-[#57606A]"}`}>
                {d.is_connected ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
                {d.is_connected ? "Connected" : "Not connected"}
              </div>
              {d.is_connected ? (
                <Button variant="outline" size="sm" data-testid={`device-disconnect-${d.id}`} disabled={busyId === d.id}
                        onClick={() => disconnect(d.id)} className="h-8 rounded-full text-xs">
                  {busyId === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "DISCONNECT"}
                </Button>
              ) : (
                <Button variant="outline" size="sm" data-testid={`device-connect-${d.id}`} disabled={busyId === d.id || !activeSub}
                        onClick={() => connect(d.id)} className="h-8 rounded-full text-xs border-[#43B02A] text-[#43B02A] hover:bg-[#43B02A]/10">
                  {busyId === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "CONNECT"}
                </Button>
              )}
              <Button variant="ghost" size="sm" data-testid={`device-edit-${d.id}`} onClick={() => openEdit(d)}
                      className="h-8 rounded-full text-[#57606A] hover:text-[#1A1C20]">
                <Pencil className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" data-testid={`device-remove-${d.id}`} onClick={() => remove(d.id)}
                      disabled={d.is_connected}
                      className="h-8 rounded-full text-[#57606A] hover:text-[#FF3B30] disabled:opacity-30">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={editId !== null} onOpenChange={(o) => !o && setEditId(null)}>
        <DialogContent className="bg-white rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle>Edit device</DialogTitle>
            <DialogDescription>Update this device's name or MAC address.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">Device name</Label>
              <Input data-testid="device-edit-name-input" value={editName} onChange={(e) => setEditName(e.target.value)}
                     className="mt-1 h-12 rounded-none" placeholder="Home phone" />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">MAC Address</Label>
              <Input data-testid="device-edit-mac-input" value={editMac} onChange={(e) => { setEditMac(formatMacInput(e.target.value)); setEditError(""); }}
                     disabled={devices.find((d) => d.id === editId)?.is_connected}
                     type="text" inputMode="text" autoComplete="off" autoCapitalize="characters"
                     className="mt-1 h-12 rounded-none font-mono disabled:opacity-50" placeholder="AA:BB:CC:11:22:33" />
              {devices.find((d) => d.id === editId)?.is_connected && (
                <p className="mt-1 text-xs text-[#57606A]">Disconnect this device first to change its MAC address.</p>
              )}
            </div>
            {editError && (
              <div data-testid="edit-device-error" className="border border-[#FF3B30]/30 bg-[#FF3B30]/5 px-3 py-2 text-sm text-[#FF3B30]">
                {editError}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditId(null)} className="rounded-none">Cancel</Button>
            <Button data-testid="device-edit-save-btn" onClick={saveEdit} className="rounded-none bg-[#43B02A] hover:bg-[#368F22] text-white font-bold">
              SAVE
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
